import { validateDate, validateMissions } from '@the-hive/lib-shared';
import { navigateComponent } from '../actions/app.action.js';
import { heroMissionsReceived } from '../actions/hero-missions-received.action.js';
import { heroQuestReceived } from '../actions/hero-quest-received.action.js';
import { heroQuestsReceived } from '../actions/hero-quests-received.action.js';
import { questCreated } from '../actions/quest-created.action';
import { questExistsErrorReceived } from '../actions/quest-exists-error-received.action.js';
import { questUpdated } from '../actions/quest-updated.action.js';
import { resetEditingQuest } from '../actions/reset-editing-quest.action.js';
import { userHasOldQuests } from '../actions/user-has-old-quests.action.js';
import auth_service from './auth.service.js';
import { BaseService } from './base.service.js';
import { get, post } from './shared.js';

export class QuestService extends BaseService{
  constructor() {
    super();
  }

  async getQuest(hero) {
    const upn = auth_service.getUserPrincipleName();

    //this check is here so that we don not get the data twice
    //if the user we are trying to get data for is the logged in user, then user-data would have already made this call
    //Gery, 2019-09-11
    if (hero === upn) return;

    const response = await get(this.buildApiUrl(`heroQuest/${hero}`));
    const data = await response.json();
    const { quest } = data;
    let { missions } = data;

    if (quest) {

      quest.startDate = new Date(quest.startDate);
      quest.endDate = new Date(quest.endDate);
      missions = missions.map(mission => {
        const dateCompleted = mission.dateCompleted ? new Date(mission.dateCompleted) : mission.dateCompleted;
        return {
          ...mission,
          dateCompleted
        };
      });

      this.store.dispatch(heroQuestReceived(quest));
      this.store.dispatch(heroMissionsReceived(missions));
    } else {
      this.store.dispatch(navigateComponent('/permission'));
    }
  }

  async getHeroQuests(upn) {
    if (!upn) upn = auth_service.getUserPrincipleName();

    const response = await get(this.buildApiUrl(`allHeroQuestsWithMissions?upn=${upn}`));
    let quests = await response.json();

    quests = quests.map(quest => {
      const startDate = new Date(quest.startDate);
      const endDate = new Date(quest.endDate);
      return {
        ...quest,
        startDate,
        endDate
      };
    });

    this.store.dispatch(heroQuestsReceived(quests));
  }

  async updateQuest(quest, missions) {
    const request = {
      quest,
      missions
    };

    const response = await post(this.buildApiUrl('updateQuest'), request);
    const data = await response.json();
    const upn = auth_service.getUserPrincipleName();
    const updatedQuest = data.quest;
    let updatedMissions = data.missions;

    updatedQuest.startDate = new Date(updatedQuest.startDate);
    updatedQuest.endDate = new Date(updatedQuest.endDate);
    updatedMissions = updatedMissions.map(mission => {
      const dateCompleted = mission.dateCompleted ? new Date(mission.dateCompleted) : mission.dateCompleted;
      return {
        ...mission,
        dateCompleted
      };
    });

    if (updatedQuest && updatedQuest.heroUserPrincipleName == upn) {
      this.store.dispatch(questUpdated({ quest: updatedQuest, missions: updatedMissions }));
    }

    if (updatedQuest && updatedQuest.heroUserPrincipleName != upn) {
      post(this.buildApiUrl(`lastGuideActiveDate?upn=${upn}`));
    }

    this.store.dispatch(resetEditingQuest());

    return data;
  }

  async createQuest(quest, missions) {
    const request = {
      quest,
      missions
    };

    validateDate(quest.endDate, 'endDate')
    validateDate(quest.startDate, 'startDate')
    validateMissions(missions)

    const response = await post(this.buildApiUrl('createQuest'), request);
    const data = await response.json();

    const updatedQuest = data.quest;
    if (data.error) {
      this.store.dispatch(questExistsErrorReceived(updatedQuest));
      return;
    }

    let updatedMissions = data.missions;
    const avatar = data.avatar;
    const numberOfPartsAvailable = data.numberOfPartsAvailable;

    if (updatedQuest) {
      updatedQuest.startDate = new Date(updatedQuest.startDate);
      updatedQuest.endDate = new Date(updatedQuest.endDate);
      updatedMissions = updatedMissions.map(mission => {
        const dateCompleted = mission.dateCompleted ? new Date(mission.dateCompleted) : mission.dateCompleted;
        return {
          ...mission,
          dateCompleted
        };
      });

      if (quest.heroUpn == auth_service.getUserPrincipleName()) {
        this.store.dispatch(questCreated({ quest: updatedQuest, missions: updatedMissions, avatar, numberOfPartsAvailable }));
      } else {
        this.store.dispatch(heroQuestReceived(updatedQuest));
      }
    }

    this.store.dispatch(resetEditingQuest());
  }

  async resumeQuest (questId) {
    const request = {
      questId
    };
    const response = await post(this.buildApiUrl('resumeQuest'), request);
    const quest = await response.json();
    quest.startDate = new Date(quest.startDate);
    quest.endDate = new Date(quest.endDate);
    this.store.dispatch(questUpdated(quest));
  }

  async getGuidesBySpecialisation(specialisationId) {
    const response = await get(this.buildApiUrl(`guides?specialisationId=${specialisationId}`));
    const guides = await response.json();
    return guides;
  }

  async getOldQuests() {
    const response = await get(this.buildApiUrl('oldQuests'));
    let oldQuests = await response.json();
    oldQuests = oldQuests.map(quest => {
      const startDate = new Date(quest.startDate);
      const endDate = new Date(quest.endDate);
      return {
        ...quest,
        startDate,
        endDate
      };
    });
    this.store.dispatch(userHasOldQuests(oldQuests));
  }

  questOptimisticUpdate(quest, missions) {
    // Optimistically update the Quest, while we wait for the response to return. - Mike, 2019-07-08
    this.store.dispatch(questUpdated({ quest, missions }));
  }

  async retractHeroQuestCompletion(quest){
    const response = await post(this.buildApiUrl('retractQuestCompletion'), {questId: quest.questId, upn: quest.heroUserPrincipleName});
    if (response) this.store.dispatch(navigateComponent('/heroes'));
  }

  async postQuestStatusChange (endpoint, quest) {
    const response = await post(this.buildApiUrl(endpoint), {questId: quest.questId, upn: quest.heroUserPrincipleName});
    const updatedQuest = await response.json();
    updatedQuest.startDate = new Date(updatedQuest.startDate);
    updatedQuest.endDate = new Date(updatedQuest.endDate);
    this.store.dispatch(questUpdated({...updatedQuest, missions: []}));
    if (updatedQuest) {
      this.store.dispatch(navigateComponent('/home'));
    } else {
      // an error happened, so we should not navigate
    }
  }

  async markQuestComplete (quest) {
    return this.postQuestStatusChange('markQuestComplete', quest);
  }

  async markQuestAbandoned (quest) {
    return this.postQuestStatusChange('markQuestAbandoned', quest);
  }

  async markQuestPaused (quest) {
    return this.postQuestStatusChange('markQuestPaused', quest);
  }
}

export default new QuestService();
